import { h, Fragment } from 'preact';
import { useCallback, useEffect, useReducer, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import MenuIcon from '../../molecules/Icon/Menu';
import { getCompanyDetails, getCompanyId } from '../../lib/Amalgam';
import { getMenuData, MENU_TYPE } from '../../lib/Menu';
import { getCurrentRouteName, isGalleryView, shouldShowLightThemeOnRoute } from '../../lib/Route';
import { getCatalogueTheme } from '../../lib/Theme';
import { clearCachedScrollPositions } from '../../lib/ScrollPosition';
import {
  isLightColor,
  isNavigatingToCurrentLink,
  isResellerBundleView,
  rgbaToIntensities,
  rgbToHex,
  shouldProcessClick
} from '../../lib/Utils';
import FacebookLogo from '../../molecules/Icon/FacebookLogo';
import YoutubeLogo from '../../molecules/Icon/YoutubeLogo';
import TwitterLogo from '../../molecules/Icon/TwitterLogo';
import InstagramLogo from '../../molecules/Icon/InstagramLogo';
import PrimaryLoader from '../PrimaryLoader';
import SideBar from '../SideBar';
import ClickOutside from '../ClickOutside';
import Tooltip from '../Tooltip';
import SubMenu from './SubMenu';
import {
  CHANGE_ACTIVE_MENU,
  defaultMenuState,
  FETCH_MENU_ITEMS,
  FETCH_MENU_ITEMS_FAILURE,
  FETCH_MENU_ITEMS_SUCCESS,
  menuReducer
} from './menu.reducer';
import './styles.less';

const getHeaderBgColor = () => {
  const headerElement = document.getElementsByClassName('header-collapsed')[0];

  if (!headerElement) {
    return;
  }

  return rgbaToIntensities(headerElement.style.backgroundColor);
};

export default ({ fromBundle, isDesktop = false, isMobileWebView = false, buttonStyle = {} }) => {
  const [showSideBarMenu, setShowSidebarMenu] = useState(false);
  const [, reRender] = useState(true);
  const activeMenuRef = useRef(null);

  const {
    themeTextColor,
    homeGrid: { backgroundColor: themeBgColor, searchIconColor: iconColor = '#FFFFFF' }
  } = getCatalogueTheme();

  const menuIconColor = shouldShowLightThemeOnRoute(getCurrentRouteName())
    ? themeTextColor
    : iconColor;

  const headerBgColorIntensities = isMobileWebView
    ? rgbaToIntensities('rgb(255, 255, 255)')
    : getHeaderBgColor();

  const headerBgColor = headerBgColorIntensities
    ? rgbToHex(...headerBgColorIntensities)
    : themeBgColor;

  const backgroundColor = isGalleryView() ? '#fff' : headerBgColor;
  const textColor = isLightColor(backgroundColor) ? '#2e2e2e' : '#f4f4f4';

  const [menuMetaData, updateMenuMetaData] = useReducer(menuReducer, defaultMenuState, null);
  const { loading, menuItems, error, activeMenus } = menuMetaData;

  useEffect(() => {
    updateMenuMetaData({ type: FETCH_MENU_ITEMS });
    getMenuData(isMobileWebView)
      .then((menuItems) => updateMenuMetaData({ type: FETCH_MENU_ITEMS_SUCCESS, menuItems }))
      .catch((error) => updateMenuMetaData({ type: FETCH_MENU_ITEMS_FAILURE, error }));
  }, []);

  useEffect(() => {
    if (error) {
      console.error('error while fetching menu items', error);
    }
  }, [error]);

  if ((isDesktop || isMobileWebView) && (loading || error)) {
    return null;
  }

  const toggleSideBarMenu = () => setShowSidebarMenu(!showSideBarMenu);

  const closeOverlaySubmenuContainer = useCallback((event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    activeMenuRef.current = null;
    updateMenuMetaData({ type: CHANGE_ACTIVE_MENU, iteration: -1 });
  }, []);

  const setActiveMenu = useCallback(
    ({ iteration, index }) =>
      (expanded, buttonRef) => {
        if (isDesktop) {
          if (iteration === 0) {
            activeMenuRef.current = buttonRef;
          }
        } else {
          activeMenuRef.current = null;
        }
        updateMenuMetaData({
          type: CHANGE_ACTIVE_MENU,
          iteration,
          activeIndex: expanded ? index : -1
        });
      },
    []
  );

  const goToLink =
    (url, openInNewWindow = false, useRoute = false) =>
      (event) => {
        if (event) {
          if (!shouldProcessClick(event)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
        }

        closeOverlaySubmenuContainer(event);
        clearCachedScrollPositions();

        if (!url) {
          setShowSidebarMenu(false);
          return;
        }

        if (isNavigatingToCurrentLink(url)) {
          setShowSidebarMenu(false);
          return;
        }

        if (openInNewWindow) {
          setShowSidebarMenu(false);
          window.open(url, '_blank');
          return;
        }

        if (useRoute) {
          setShowSidebarMenu(false);
          route(url);
          return;
        }

        // TODO: need to handle group to group routing in app/index.js
        // Change href, and not pathname, to ensure that url query params are not lost
        window.location.href = url;
      };

  const renderMenuButton = ({ menuItem, index, key, className, iteration = 0 }) => {
    const { title, linkType: menuItemType } = menuItem;
    const commonStyle = {
      color: textColor
    };
    const commonProps = {};

    if (isDesktop) {
      commonProps.onMouseEnter = (e) => setActiveMenu({ iteration, index })(false, e);
    }

    if (!isDesktop) {
      commonStyle.paddingLeft = `${iteration * 15 + 20}px`;
    }

    switch (menuItemType) {
      case MENU_TYPE.CATALOGUE_LINK:
      case MENU_TYPE.OTHER_PAGE_LINK: {
        const { linkPath, useRoute } = menuItem;
        const isCompanyDetailsPage =
          linkPath.startsWith('/company/') && linkPath.endsWith(`/${getCompanyId()}`);
        return (
          <li
            key={key}
            {...commonProps}
            className={`${className} page-link`}
            style={commonStyle}
            itemProp="name"
          >
            <a
              title={title}
              href={linkPath}
              target={isCompanyDetailsPage ? '_blank' : '_self'}
              onClick={goToLink(linkPath, isCompanyDetailsPage ? true : undefined, useRoute)}
              className="ellipsis"
              itemProp="url" rel="noreferrer"
            >
              {title}
            </a>
          </li>
        );
      }

      case MENU_TYPE.CUSTOM_LINK:
      case MENU_TYPE.EXTERNAL_LINK: {
        const { linkUrl, onClick, shouldShow } = menuItem;
        let onClickMenu = goToLink(linkUrl, true);
        let menuClassName = 'external-link';

        if (menuItemType === MENU_TYPE.CUSTOM_LINK) {
          onClickMenu = (...params) => {
            onClick(...params);
            clearCachedScrollPositions();
            closeOverlaySubmenuContainer(...params);
          };

          menuClassName = 'custom-link';
        }

        if (
          typeof onClickMenu !== 'function' ||
          (typeof shouldShow === 'function' && !shouldShow())
        ) {
          return null;
        }

        return (
          <li
            key={key}
            {...commonProps}
            className={`${className} ${menuClassName || ''}`}
            style={commonStyle}
            itemProp="name"
          >
            {menuItemType === MENU_TYPE.CUSTOM_LINK ? (
              <span onClick={onClickMenu} className="ellipsis">
                {title}
              </span>
            ) : (
              <a
                title={title}
                href={linkUrl}
                target={menuItemType === MENU_TYPE.EXTERNAL_LINK ? '_blank' : '_self'}
                onClick={onClickMenu}
                className="ellipsis"
                itemProp="url"
                rel="noreferrer noopener nofollow"
              >
                {title}
              </a>
            )}
          </li>
        );
      }

      case MENU_TYPE.SUB_MENU: {
        const { menuItems: subMenuList } = menuItem;

        if (!Array.isArray(subMenuList) || !subMenuList.length) {
          return null;
        }

        const isActive = activeMenus[iteration] === index;

        return (
          <SubMenu
            key={key}
            className={`${className}`}
            menuItem={menuItem}
            style={{
              ...commonStyle,
              ...(isDesktop && iteration && isActive
                ? { backgroundColor: commonStyle.color + '13' } // 7.5% opacity
                : {})
            }}
            isDesktop={isDesktop}
            isActive={isActive}
            onToggleState={setActiveMenu({ iteration, index })}
            subMenus={() => {
              if (isDesktop) {
                if (iteration !== 0) {
                  return null;
                }
                return renderActiveSubMenuOptions();
              }

              return (
                <ul itemType="http://www.schema.org/SiteNavigationElement" itemScope>
                  {subMenuList.map((subMenu) =>
                    renderMenuItem({
                      key: `${key}_${subMenu.title}`,
                      className: 'menu-item',
                      menuItem: subMenu,
                      iteration
                    })
                  )}
                </ul>
              );
            }}
          />
        );
      }
      default:
        return null;
    }
  };

  const renderMenuItem = ({
    menuItem,
    index,
    className = '',
    iteration = -1,
    key = 'menu-item'
  }) => {
    if (!menuItem) {
      return null;
    }

    const { title, className: menuClassName } = menuItem;

    return renderMenuButton({
      menuItem,
      index,
      key: `${key}_${title}`,
      iteration: iteration + 1,
      className: `menu-item-btn${menuClassName ? ` ${menuClassName}` : ''} ${className} ${
        isMobileWebView ? 'menu-item-btn-webview' : ''
      }`
    });
  };

  const renderActiveSubMenuOptions = () => {
    if (!activeMenus || !activeMenus.length) {
      return null;
    }

    const subMenuContent = [];
    let activeSubMenus = menuItems;

    for (let activeMenuIndex = 0; activeMenuIndex < activeMenus.length; activeMenuIndex++) {
      const activeMenuOption = activeSubMenus[activeMenus[activeMenuIndex]];

      if (!activeMenuOption) {
        break;
      }

      activeSubMenus = activeMenuOption.menuItems;

      if (!Array.isArray(activeSubMenus) || !activeSubMenus.length) {
        break;
      }

      subMenuContent.push(
        <ul
          className="sub-menu-item-items"
          style={{ borderRightColor: textColor + '1a' }}
          itemType="http://www.schema.org/SiteNavigationElement"
          itemScope
        >
          {activeSubMenus.map((activeSubMenu, index) => {
            const { title } = activeSubMenu;
            return renderMenuItem({
              menuItem: activeSubMenu,
              index,
              iteration: activeMenuIndex,
              key: `sub-menu_${title}`
            });
          })}
        </ul>
      );
    }

    return (
      <Tooltip
        parentElement={activeMenuRef}
        resetVisibility={() => reRender((prev) => !prev)}
        isVisible
      >
        <ClickOutside onClickOutside={closeOverlaySubmenuContainer} closeOnHoverLeave>
          <div
            className="desktop-sub-menu-card"
            style={{
              backgroundColor,
              boxShadow: `0px 1px 10px 0px ${textColor}1b`,
              border: `1px solid ${textColor}1b`
            }}
          >
            {subMenuContent}
          </div>
        </ClickOutside>
      </Tooltip>
    );
  };

  const renderMenuItems = () => {
    if (loading) {
      return (
        <div className="loading-menus">
          <PrimaryLoader />
        </div>
      );
    }

    if (!Array.isArray(menuItems)) {
      return null;
    }

    const menuItemsButtons = menuItems.map((menuItem, index) =>
      renderMenuItem({ menuItem, index })
    );

    if (isDesktop) {
      return (
        <ul className="menu-items" itemType="http://www.schema.org/SiteNavigationElement" itemScope>
          {menuItemsButtons}
        </ul>
      );
    }

    return menuItemsButtons;
  };

  const renderSocialLinks = () => {
    const {
      facebook: facebookAccount,
      instagram: instagramAccount,
      youtube: youtubeAccount,
      twitter: twitterAccount
    } = getCompanyDetails();

    if (!facebookAccount && !instagramAccount && !youtubeAccount && !twitterAccount) {
      return null;
    }

    return (
      <ul className="social-links">
        {!!facebookAccount && (
          <li className="social-btn">
            <a
              href={facebookAccount}
              target="_blank"
              onClick={goToLink(facebookAccount, true)}
              rel="noreferrer noopener nofollow"
              title="facebook"
            >
              <FacebookLogo />
            </a>
          </li>
        )}
        {!!instagramAccount && (
          <li className="social-btn">
            <a
              href={instagramAccount}
              target="_blank"
              onClick={goToLink(instagramAccount, true)}
              rel="noreferrer noopener nofollow"
              title="instagram"
            >
              <InstagramLogo />
            </a>
          </li>
        )}
        {!!twitterAccount && (
          <li className="social-btn">
            <a
              href={twitterAccount}
              target="_blank"
              onClick={goToLink(twitterAccount, true)}
              rel="noreferrer noopener nofollow"
              title="youtube"
            >
              <TwitterLogo />
            </a>
          </li>
        )}
        {!!youtubeAccount && (
          <li className="social-btn">
            <a
              href={youtubeAccount}
              target="_blank"
              onClick={goToLink(youtubeAccount, true)}
              rel="noreferrer noopener nofollow"
              title="twitter"
            >
              <YoutubeLogo />
            </a>
          </li>
        )}
      </ul>
    );
  };

  // mobile view
  if (!isDesktop && !isMobileWebView) {
    return (
      <Fragment>
        {isResellerBundleView(fromBundle) || (Array.isArray(menuItems) && menuItems.length) ? (
          <button
            type="button"
            title="open drawer"
            className="menu-button"
            onClick={toggleSideBarMenu}
            style={buttonStyle}
          >
            <MenuIcon color={menuIconColor} className="menu-icon" />
          </button>
        ) : null}
        <SideBar
          color={textColor}
          style={{ backgroundColor, color: textColor }}
          open={showSideBarMenu}
          onClose={toggleSideBarMenu}
          renderMenuItems={renderMenuItems}
          renderSocialLinks={renderSocialLinks}
          fromBundle={fromBundle}
        />
      </Fragment>
    );
  }

  if (isMobileWebView) {
    return (
      <nav
        className="sidebar-menu sidebar-menu-webview"
        style={{ color: textColor, backgroundColor }}
      >
        <ul className="menu-bar" itemType="http://www.schema.org/SiteNavigationElement" itemScope>
          {renderMenuItems()}
        </ul>
        <ul className="footer" itemType="http://www.schema.org/SiteNavigationElement" itemScope>
          {renderSocialLinks()}
        </ul>
      </nav>
    );
  }

  // desktop view
  return renderMenuItems();
};
